import {
  FC,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Region } from '../../../utils';
import { UseAuthContextType } from '../auth0/auth.types';
import { getDefaultUseAuthState } from '../auth0/helpers';
import { getRegionFromTokenOrDefault } from '../auth0/jwt';

type TestAuthContextType = UseAuthContextType;

const TestAuthDefaultState = getDefaultUseAuthState({
  user: {
    name: 'e2e',
    picture: undefined,
    email: 'email@email.com',
    email_verified: true,
  },
});

const TestAuthContext =
  createContext<TestAuthContextType>(TestAuthDefaultState);

export const AuthenticationProvider: FC<{
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
}> = ({ onTokenReceived, children }) => {
  const [token, setToken] = useState<TestAuthContextType['token']>();

  const checkSession = (polling?: boolean) => {
    const tokenItem = window.localStorage.getItem('confidence_test_ls_ak');

    if (tokenItem) {
      setToken(tokenItem);
    } else if (polling) {
      setTimeout(() => checkSession(polling), 1000);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const loginWithPopup = useCallback(async () => {
    checkSession(true);
  }, [checkSession]);

  const value = useMemo<TestAuthContextType>(
    () => ({
      ...TestAuthDefaultState,
      token,
      isAuthenticated: !!token,
      loginWithPopup,
    }),

    [TestAuthDefaultState, token, loginWithPopup],
  );

  useEffect(() => {
    if (value.token) {
      onTokenReceived(value.token, getRegionFromTokenOrDefault(value.token));
    }
  }, [value]);

  return (
    <TestAuthContext.Provider value={value}>
      {children}
    </TestAuthContext.Provider>
  );
};

export const useAuth = () => useContext(TestAuthContext);
